// api.ts — thin wrapper around the admin ConnectRPC endpoints.
// Admin tokens live under a different localStorage key (`mp_admin_token`)
// than photographer (`mp_token`) or consumer (`mp_consumer_token`)
// tokens so an XSS on the public app can't steal admin sessions and
// vice versa. The cookie-domain separation further enforces this at
// the browser level, but we also namespace localStorage out of caution.

export const ADMIN_TOKEN_KEY = 'mp_admin_token';
export const ADMIN_REFRESH_KEY = 'mp_admin_refresh';

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminTokens(access: string, refresh: string): void {
  localStorage.setItem(ADMIN_TOKEN_KEY, access);
  localStorage.setItem(ADMIN_REFRESH_KEY, refresh);
}

export function clearAdminTokens(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
}

// Clear stale tokens on every page load. The admin JWT has a 15-minute
// lifetime; a long-idle session is always re-authed rather than silently
// extended with the refresh token (intentional — admin sessions are
// higher-risk than photographer/consumer).
function clearExpiredAdminTokens() {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) {
      clearAdminTokens();
    }
  } catch {
    clearAdminTokens();
  }
}
clearExpiredAdminTokens();

export class AdminAPIError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * Low-level call against an admin service. Returns the parsed JSON or
 * throws AdminAPIError. Callers usually use one of the typed wrappers
 * below (platformAuth.*) rather than invoking adminCall directly.
 */
export async function adminCall<T = any>(service: string, method: string, body: unknown = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getAdminToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const resp = await fetch(`/${service}/${method}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (resp.status === 401) {
    clearAdminTokens();
    window.dispatchEvent(new Event('mp-admin-auth-expired'));
  }

  let parsed: any = null;
  try {
    parsed = await resp.json();
  } catch {
    // no body
  }

  if (!resp.ok) {
    const msg = parsed?.message || parsed?.error || resp.statusText;
    const code = parsed?.code || String(resp.status);
    throw new AdminAPIError(resp.status, code, msg);
  }
  return parsed as T;
}

// ───── PlatformAuthService wrappers ─────

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface PlatformUserSummary {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  lastLoginAt?: string;
  totpEnrolled?: boolean;
  totpEnrolledAt?: string;
}

export interface LoginResponse {
  enrollmentRequired?: {
    enrollmentToken: string;
    otpauthUrl: string;
    qrCodePngBase64: string;
    backupCodes: string[];
  };
  twoFactorRequired?: {
    challengeToken: string;
  };
  tokensIssued?: {
    tokens: TokenPair;
    user: PlatformUserSummary;
    dev_2FaBypassed?: boolean;
  };
}

export const platformAuth = {
  login: (usernameOrEmail: string, password: string) =>
    adminCall<LoginResponse>('momentpix.admin.v1.PlatformAuthService', 'Login', {
      usernameOrEmail,
      password,
    }),

  enroll2FA: (enrollmentToken: string, totpCode: string) =>
    adminCall<{ tokens: TokenPair; user: PlatformUserSummary }>(
      'momentpix.admin.v1.PlatformAuthService',
      'Enroll2FA',
      { enrollmentToken, totpCode },
    ),

  verify2FA: (challengeToken: string, code: string) =>
    adminCall<{ tokens: TokenPair; user: PlatformUserSummary }>(
      'momentpix.admin.v1.PlatformAuthService',
      'Verify2FA',
      { challengeToken, code },
    ),

  logout: () =>
    adminCall<{ ok: boolean }>('momentpix.admin.v1.PlatformAuthService', 'Logout', {}),

  whoAmI: () =>
    adminCall<{
      user: PlatformUserSummary;
      stepupActive: boolean;
      impersonatedById: string;
    }>('momentpix.admin.v1.PlatformAuthService', 'WhoAmI', {}),

  stepUp: (password: string) =>
    adminCall<{ ok: boolean; expiresIn: number }>(
      'momentpix.admin.v1.PlatformAuthService',
      'StepUp',
      { password },
    ),

  startImpersonation: (req: { targetUserId: string; targetOrgId?: string; reason: string }) =>
    adminCall<{
      code: string;
      redeemUrl: string;
      targetUserId: string;
      targetUserEmail: string;
      targetUserName: string;
      targetOrgId: string;
      targetOrgName: string;
      targetRole: string;
      expiresAt: string;
    }>('momentpix.admin.v1.PlatformAuthService', 'StartImpersonation', req),
};

// ───── PlatformAuditService wrappers ─────

export interface AuditRow {
  id: number;
  occurredAt: string;
  platformUserId: string;
  platformUsername: string;
  impersonatedById: string;
  actorSubjectId: string;
  actorSubjectKind: string;
  action: string;
  targetKind: string;
  targetId: string;
  requestIp: string;
  requestUserAgent: string;
  requestId: string;
  beforeStateJson: string;
  afterStateJson: string;
  metadataJson: string;
  success: boolean;
  errorMessage: string;
}

export interface QueryAuditRequest {
  platformUserId?: string;
  actionPrefix?: string;
  targetKind?: string;
  targetId?: string;
  sinceRfc3339?: string;
  untilRfc3339?: string;
  success?: 'SUCCESS_FILTER_UNSPECIFIED' | 'SUCCESS_FILTER_ONLY_SUCCESS' | 'SUCCESS_FILTER_ONLY_FAILURE';
  impersonationOnly?: boolean;
  limit?: number;
  beforeId?: number;
}

export const platformAudit = {
  query: (req: QueryAuditRequest) =>
    adminCall<{ rows: AuditRow[]; nextBeforeId: number; hasMore: boolean }>(
      'momentpix.admin.v1.PlatformAuditService',
      'QueryAudit',
      req,
    ),

  get: (id: number) =>
    adminCall<{ row: AuditRow }>('momentpix.admin.v1.PlatformAuditService', 'GetAuditRow', { id }),
};
